import { supportedEffects } from './utils'

/**
 * Function to create timeline markers for effects in the text
 * @param {string} text - Text with effect tags
 * @returns {Array} - Array of marker objects with position and effectType
 */
function getTimelineMarkers(text) {
  const markers = []
  const regex = /<(\w+)>/g

  let match = regex.exec(text)
  while (match !== null) {
    const effectType = match[1]
    const effect = supportedEffects.find(e => e.name === effectType)

    // Calculate position as percentage of the total text length
    const position = match.index / text.length

    markers.push({
      position,
      effectType,
      color: effect?.color || '#e0e7ff',
      textColor: effect?.textColor || '#4338ca',
    })

    match = regex.exec(text)
  }

  return markers
}

/**
 * Timeline component to visualize effects with vertical spacing lines
 * @param {string} text - Text with effect tags
 */
function EffectsTimeline({ text }) {
  if (!text) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <p className="text-xs text-gray-500">
            Type
            {' '}
            <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded text-2xs">&lt;</kbd>
            {' '}
            to insert effects
          </p>

          <div className="flex flex-wrap gap-2">
            {supportedEffects.map(effect => (
              <div
                key={effect.name}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border"
                style={{
                  backgroundColor: effect.color,
                  color: effect.textColor,
                  borderColor: `${effect.textColor}20`,
                }}
              >
                {effect.name}
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const markers = getTimelineMarkers(text)
  const numberOfGridLines = 6 // Similar to the screenshot showing 6 timestamp marks

  return (
    <div className="flex flex-col gap-4">
      {/* Timeline with vertical spacing lines and effect markers */}
      <div className="relative">
        {markers.length === 0 && (
          <span className="flex flex-col pb-2">
            <label className="font-medium text-sm">
              Effects Timeline
            </label>
          </span>
        )}
        {/* Timeline container */}
        <div className="relative h-8 bg-gray-50 border border-gray-200 rounded-sm">
          {/* Vertical spacing lines */}
          {Array.from({ length: numberOfGridLines }, (_, i) => (
            <div
              key={i}
              className="absolute top-0 w-px h-full bg-gray-300"
              style={{ left: `${(i / (numberOfGridLines - 1)) * 100}%` }}
            />
          ))}

          {/* Effect markers */}
          {markers.map((marker, index) => (
            <div
              key={index}
              className="absolute top-0 h-full flex flex-col items-center justify-center"
              style={{ left: `${marker.position * 100}%` }}
            >
              {/* Marker line */}
              <div
                className="absolute top-0 w-0.5 h-full"
                style={{
                  backgroundColor: marker.textColor,
                  transform: 'translateX(-1px)',
                }}
              />

              {/* Effect label */}
              <div
                className="absolute -top-8 px-2 py-1 rounded text-xs font-medium whitespace-nowrap"
                style={{
                  backgroundColor: marker.color,
                  color: marker.textColor,
                  transform: 'translateX(-50%)',
                }}
              >
                {marker.effectType}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Instructions and effect tags */}
      <div className="flex flex-col gap-2">
        <p className="text-xs text-gray-500">
          Type
          {' '}
          <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded text-2xs">&lt;</kbd>
          {' '}
          to insert effects
        </p>

        <div className="flex flex-wrap gap-2">
          {supportedEffects.map(effect => (
            <div
              key={effect.name}
              className="inline-flex items-center px-3 py-1 rounded-sm text-xs font-medium border"
              style={{
                backgroundColor: effect.color,
                color: effect.textColor,
                borderColor: `${effect.textColor}20`,
              }}
            >
              {effect.name}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default EffectsTimeline
